import dayjs from 'dayjs';
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';

import { RoleTypeDto } from '@/api/generated/models/role-type-dto';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { LocalStorageParam } from '@/utils/localStorage.util.ts';
import { useGetEmployeeDetails } from '@/views/userDetails/util/useGetEmployeeDetails.util.ts';

const DAYS_TO_WAIT = 7;

type Return = {
  closeDialog: () => void;
  isDialogOpen: boolean;
  openEditProfile: () => Promise<void>;
  shouldShowWarning: boolean;
};

const shouldShowWarningAgain = (): boolean => {
  const dismissedDate = localStorage.getItem(LocalStorageParam.LRM_WARNING_DISMISSED_KEY);
  if (!dismissedDate) {
    return true;
  }

  const lastDismissed = dayjs(dismissedDate);
  const daysPassed = dayjs().diff(lastDismissed, 'day');

  return daysPassed >= DAYS_TO_WAIT;
};

export const useLrmOperatingLocationsWarning = (onOpenEditDialog: () => Promise<void>): Return => {
  const { user } = useUserContext();
  const { employeeDetails, getEmployeeDetails } = useGetEmployeeDetails();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const hasCheckedRef = useRef(false);

  const isLRM = user.role === RoleTypeDto.LocalResourceManager;
  const hasNoOperatingLocations = employeeDetails?.operatingLocations?.length === 0;
  const canShowWarning = shouldShowWarningAgain();

  const shouldShowWarning = isLRM && hasNoOperatingLocations && canShowWarning;

  const getEmployeeDetailsEffect = useEffectEvent(() => getEmployeeDetails(true));

  useEffect(() => {
    if (isLRM && user.dataReady && !hasCheckedRef.current) {
      hasCheckedRef.current = true;
      void getEmployeeDetailsEffect();
    }
  }, [isLRM, user.dataReady]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (shouldShowWarning && hasCheckedRef.current) {
      timeoutId = setTimeout(() => setIsDialogOpen(true), 0);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [shouldShowWarning, employeeDetails]);

  const closeModal = useCallback(() => {
    setIsDialogOpen(false);
    localStorage.setItem(LocalStorageParam.LRM_WARNING_DISMISSED_KEY, dayjs().toISOString());
  }, []);

  const openEditProfile = useCallback(async () => {
    setIsDialogOpen(false);
    await onOpenEditDialog();
  }, [onOpenEditDialog]);

  return {
    closeDialog: closeModal,
    isDialogOpen: isDialogOpen,
    openEditProfile,
    shouldShowWarning,
  };
};
